import {
  DEFAULT_LOADABLE_STEP_KG,
  DEFAULT_LOADABLE_STEP_LB,
} from "./plateCalculator.js";
import { fromCanonicalKg, toCanonicalKg } from "./units.js";

// Floor a weight to the finest increment the athlete can actually load,
// in the row's display unit (LG-15). A %1RM prescription resolves to
// numbers like 68.38 kg that no plate set can build; flooring ("round
// DOWN") snaps to a loadable weight without ever prescribing MORE than
// the plates allow — erring light, never heavy.
//
// **Scope:** display + seed ONLY. Never applied to the saved prescription
// target (which stays the exact `targetWeight.percentage`); the caller
// runs this on the seeded logger cell, so the athlete sees a usable number
// while the plan's math is untouched.
//
// Unit-aware because the athlete reads the machine in the unit they picked
// per row: a kg row floors to the kg step (from their plate inventory, else
// DEFAULT_LOADABLE_STEP_KG); a lb row floors to the lb step (no lb inventory
// is modeled yet → DEFAULT_LOADABLE_STEP_LB). Returns canonical kg, so the
// caller formats it back through fromCanonicalKg like any stored weight.

export type LoadableContext = {
  /** Finest loadable increment in kg, for kg-unit rows. Derived from the
   *  athlete's configured plate inventory (smallestLoadableStepKg); omit to
   *  fall back to DEFAULT_LOADABLE_STEP_KG (the un-configured default). */
  stepKg?: number;
  /** Finest loadable increment in lb, for lb-unit rows. No lb plate
   *  inventory exists yet, so this defaults to DEFAULT_LOADABLE_STEP_LB. */
  stepLb?: number;
};

const stepFor = (unit: "kg" | "lb", ctx: LoadableContext): number =>
  unit === "kg"
    ? ctx.stepKg ?? DEFAULT_LOADABLE_STEP_KG
    : ctx.stepLb ?? DEFAULT_LOADABLE_STEP_LB;

/**
 * The next loadable rung ABOVE a weight — the mirror of `roundLoadable`, which
 * floors. Lives here rather than in the caller because "what weights can this
 * athlete actually build" is one concept and it already owns it: a second
 * module picking its own step is how two ideas of a loadable weight start to
 * disagree.
 *
 * Used by the set proposal (EQ-2 §7.4): "more load at the same reps" has to
 * land on a number the athlete can put on the bar, and erring UP by one rung
 * is correct here — the whole point is to ask for slightly more. `rungs`
 * climbs further for the trade option (heavier load, fewer reps).
 *
 * Returns canonical kg. A non-finite or non-positive input passes through
 * untouched, same contract as `roundLoadable`.
 */
export const nextLoadableUp = (
  weightKg: number,
  unit: "kg" | "lb",
  ctx: LoadableContext = {},
  rungs = 1,
): number => {
  if (!Number.isFinite(weightKg) || weightKg <= 0) return weightKg;
  const step = stepFor(unit, ctx);
  if (!Number.isFinite(step) || step <= 0 || rungs < 1) return weightKg;
  const display = fromCanonicalKg(weightKg, unit);
  // Mirror of roundLoadable's epsilon: a weight already exactly on a rung
  // must climb a full rung, not be nudged by float error into staying put.
  const current = Math.floor(display / step + 1e-6);
  return toCanonicalKg((current + rungs) * step, unit);
};

export const roundLoadable = (
  weightKg: number,
  unit: "kg" | "lb",
  ctx: LoadableContext = {},
): number => {
  if (!Number.isFinite(weightKg) || weightKg <= 0) return weightKg;
  const step = stepFor(unit, ctx);
  if (!Number.isFinite(step) || step <= 0) return weightKg;

  const display = fromCanonicalKg(weightKg, unit);
  // +epsilon so a weight already sitting exactly on a step (e.g. 67.5 with
  // step 2.5 → 27.0) isn't dropped a whole rung by IEEE float error.
  const rungs = Math.floor(display / step + 1e-6);
  const floored = rungs * step;
  // A sub-step target (target < one increment) floors to 0 — that would wipe
  // a light prescription to an empty bar. Keep the original rather than show
  // 0; a finer increment for light work is a Layer-1 equipment concern.
  if (floored <= 0) return weightKg;
  return toCanonicalKg(floored, unit);
};
